import type { Task } from '@/types/task';
import { TaskCategory } from '@/types/task';
import { getTaskStatus } from '@/utils/task-display-utils';
import { CATEGORY_CONFIG } from '@/utils/category-utils';
import { toLocalISODate } from '@/utils/date-utils';

export type PrepFilter = 'all' | 'due' | 'overdue' | 'blocked';

/**
 * All 18 TaskCategory members in canonical enum order.
 * Used to preserve category display order in grouped task lists.
 */
export const CANONICAL_CATEGORY_ORDER: TaskCategory[] = [
  TaskCategory.FoodSource,
  TaskCategory.StandPrep,
  TaskCategory.TrailCameras,
  TaskCategory.Scouting,
  TaskCategory.GearEquipment,
  TaskCategory.PropertyInfrastructure,
  TaskCategory.HabitatManagement,
  TaskCategory.RegulatoryAdmin,
  TaskCategory.PostSeason,
  TaskCategory.PredatorControl,
  TaskCategory.WaterholeManagement,
  TaskCategory.WildlifeStructures,
  TaskCategory.MineralNutrition,
  TaskCategory.NeighborAccess,
  TaskCategory.HerdData,
  TaskCategory.MockScrapes,
  TaskCategory.TreePlanting,
  TaskCategory.Other,
];

// Sanity check: CATEGORY_CONFIG and CANONICAL_CATEGORY_ORDER must cover the same keys
// (compile-time enforced by the type system via the Record<TaskCategory, CategoryConfig>)
const _exhaustiveCheck: Record<TaskCategory, unknown> = CATEGORY_CONFIG;

/**
 * Single status classifier — the ONLY entry point for deriving task status on this screen.
 *
 * Maps `getTaskStatus`'s 'pending' value to 'open' for the UI layer.
 * Precedence: DONE → BLOCKED → SNOOZED → OVERDUE → OPEN (first match).
 * `today` must be a local-start-of-day ISO date string (YYYY-MM-DD).
 *
 * All chip counts, group counts, and the header total derive from this function —
 * never from a parallel classifier or an ad-hoc completed/blocked flag check.
 */
export type DerivedStatus = 'done' | 'blocked' | 'snoozed' | 'overdue' | 'open';

export function deriveSingleStatus(task: Task, todayStr: string): DerivedStatus {
  const raw = getTaskStatus(task, todayStr);
  // 'pending' in getTaskStatus === 'open' in our UI status vocabulary
  return raw === 'pending' ? 'open' : raw;
}

/**
 * Filters tasks for the Prep task list based on the selected PrepFilter.
 *
 * Single-classification: all predicates use deriveSingleStatus.
 *
 * - all:     ALL tasks (including done and snoozed)
 * - due:     OPEN only (actionable: not overdue, blocked, snoozed, or done).
 *            Ratified: "Due" = OPEN, NO date-window restriction (RULE-344, M100).
 *            The former dueDays<=14 window has been removed intentionally.
 *            Regression lock: an OPEN task with dueDate=today+30 IS counted as Due.
 * - overdue: OVERDUE only
 * - blocked: BLOCKED only
 */
export function filterOpenTasksForPrep(tasks: Task[], filter: PrepFilter, today: Date): Task[] {
  const todayStr = toLocalISODate(today);

  return tasks.filter((task) => {
    const status = deriveSingleStatus(task, todayStr);

    switch (filter) {
      case 'all':
        return true;
      case 'due':
        // Due = OPEN (actionable). No date-window restriction.
        // Old code: return status !== 'done' && status === 'pending' && dueDays > 0 && dueDays <= 14;
        // New (RULE-344): Due = OPEN only — any OPEN task with any future/no deadline.
        return status === 'open';
      case 'overdue':
        return status === 'overdue';
      case 'blocked':
        return status === 'blocked';
    }
  });
}

/**
 * Counts tasks for each PrepFilter option, for rendering segmented-control badges.
 * All counts derive from filterOpenTasksForPrep (single classifier path).
 * Invariant: counts.all === header total (all tasks), sum(due+overdue+blocked+done+snoozed) === all.
 */
export function countPrepFilters(tasks: Task[], today: Date): Record<PrepFilter, number> {
  return {
    all: filterOpenTasksForPrep(tasks, 'all', today).length,
    due: filterOpenTasksForPrep(tasks, 'due', today).length,
    overdue: filterOpenTasksForPrep(tasks, 'overdue', today).length,
    blocked: filterOpenTasksForPrep(tasks, 'blocked', today).length,
  };
}

/**
 * Sorts tasks within a group by status priority then dueDate then sortOrder.
 * Within-group order: OVERDUE first → soonest dueDate → sortOrder (DASH-TD1 §5).
 */
export function sortTasksInGroup(tasks: Task[], todayStr: string): Task[] {
  return [...tasks].sort((a, b) => {
    const aStatus = deriveSingleStatus(a, todayStr);
    const bStatus = deriveSingleStatus(b, todayStr);
    // OVERDUE sorts first
    const aOverdue = aStatus === 'overdue' ? 0 : 1;
    const bOverdue = bStatus === 'overdue' ? 0 : 1;
    if (aOverdue !== bOverdue) return aOverdue - bOverdue;
    // Then soonest dueDate
    if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
    // Then sortOrder
    return a.sortOrder - b.sortOrder;
  });
}

/**
 * Groups ALL tasks by TaskCategory, preserving the canonical enum order.
 * Returns a Map with only categories that have at least one task.
 * Empty categories are omitted entirely.
 *
 * CRITICAL: tasks with undefined/null category route into TaskCategory.Other.
 * Previously these were silently dropped — that was a bug (DASH-TD1 §3 fix).
 */
export function groupByCategory(tasks: Task[]): Map<TaskCategory, Task[]> {
  const result = new Map<TaskCategory, Task[]>();

  for (const category of CANONICAL_CATEGORY_ORDER) {
    const matching = tasks.filter((t) => {
      // null/undefined category → route into 'other' group
      const effectiveCategory = t.category ?? TaskCategory.Other;
      return effectiveCategory === category;
    });
    if (matching.length > 0) {
      result.set(category, matching);
    }
  }

  return result;
}

/**
 * Finds the predecessor task with the soonest due date among incomplete predecessors.
 * Returns null if no incomplete predecessors exist.
 *
 * Multi-predecessor precedence (DASH-TD1 §4 / A3), ratified: soonest due date wins;
 * tiebreak on lower sortOrder; final tiebreak on lexicographic title.
 */
export function findSoonestDuePredecessor(
  task: Task,
  taskMap: Map<string, Task>,
): Task | null {
  if (!task.dependsOn || task.dependsOn.length === 0) return null;

  const incompletePredecessors = task.dependsOn
    .map((id) => taskMap.get(id))
    .filter((t): t is Task => t !== undefined && !t.completed);

  if (incompletePredecessors.length === 0) return null;

  return incompletePredecessors.reduce((best, candidate) => {
    // Soonest dueDate wins
    if (candidate.dueDate < best.dueDate) return candidate;
    if (candidate.dueDate > best.dueDate) return best;
    // Tiebreak: lower sortOrder wins
    if (candidate.sortOrder !== best.sortOrder) {
      return candidate.sortOrder < best.sortOrder ? candidate : best;
    }
    // Final tiebreak: lexicographic title
    return candidate.title < best.title ? candidate : best;
  });
}
